import { useState } from 'react';
import { Search, RefreshCw, Shield, Pencil } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import { useUserStore } from '../../zustand/UserStore';
import type { UserType } from '../../types/settings';

interface UserTableProps {
  users: UserType[];
  isLoading: boolean;
  error: string;
  onRefresh: () => void;
  onActionClick: (user: UserType) => void;
  onEditClick: (user: UserType) => void;
  refreshing: boolean;
  actionLoading: boolean;
}

export default function UserTable({ 
  users, 
  isLoading, 
  error, 
  onRefresh, 
  onActionClick, 
  onEditClick,
  refreshing,
  actionLoading 
}: UserTableProps) {
  const darkMode = useTheme((state) => state.theme);
  const currentUser = useUserStore((state) => state.user);
  const [searchQuery, setSearchQuery] = useState('');

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Admin': return 'bg-purple-500 text-white';
      case 'User': return 'bg-blue-500 text-white';
      case 'Editor': return 'bg-cyan-500 text-white';
      case 'Viewer': return 'bg-green-500 text-white';
      default: return 'bg-gray-400 text-white';
    }
  };

  const filteredUsers = users.filter(filterUser => {
    // First apply search filter
    const matchesSearch = filterUser.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        filterUser.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        filterUser.role.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Then apply hierarchical role-based filter
    if (!currentUser) return false;
    
    const currentUserRole = currentUser.role?.toLowerCase();
    const currentUserDesignation = currentUser.designation?.toLowerCase();
    const filterUserRole = filterUser.role?.toLowerCase();
    
    // Super Admin hierarchical filtering
    if (currentUserRole?.includes('super') || currentUserDesignation?.includes('super') ||
        currentUserRole === 'super admin' || currentUserDesignation === 'super admin') {
      
      // Exclude themselves
      if (filterUser.uid === currentUser.uid) return false;
      
      // Find admins directly created by this Super Admin
      const isDirectlyCreatedAdmin = filterUser.head === currentUser.uid && 
          (filterUserRole?.includes('admin') && !filterUserRole?.includes('super'));
      
      // Find users directly created by this Super Admin
      const isDirectlyCreatedUser = filterUser.head === currentUser.uid;
      
      // Find users created by admins that this Super Admin created
      const createdAdmins = users.filter(u => 
          u.head === currentUser.uid && 
          u.role?.toLowerCase()?.includes('admin') && 
          !u.role?.toLowerCase()?.includes('super')
      );
      const isUserCreatedByMyAdmin = createdAdmins.some(admin => admin.uid === filterUser.head);
      
      return isDirectlyCreatedAdmin || isDirectlyCreatedUser || isUserCreatedByMyAdmin;
    }
    
    // Admin can only see users they created
    if (currentUserRole?.includes('admin') || currentUserDesignation?.includes('admin') ||
        currentUserRole === 'admin' || currentUserDesignation === 'admin') {
      
      // Exclude themselves
      if (filterUser.uid === currentUser.uid) return false;
      
      // Only show users created by this admin
      return filterUser.head === currentUser.uid && 
             (filterUserRole === 'user' || filterUserRole === 'representative' ||
              (!filterUserRole?.includes('admin') && !filterUserRole?.includes('super')));
    }
    
    // Regular users cannot see other users
    return false;
  });

  return (
    <div className={`rounded-xl sm:rounded-2xl shadow-lg overflow-hidden ${
      darkMode 
        ? 'bg-[#0a1414] border border-[#1a2424]' 
        : 'bg-white border border-gray-200'
    }`}>
      <div className={`p-4 sm:p-6 border-b ${
        darkMode ? 'border-[#1a2424]' : 'border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <h2 className={`text-lg sm:text-xl font-semibold ${
            darkMode ? 'text-white' : 'text-[#053333]'
          }`}>Team Members</h2>
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-initial">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                className={`w-full sm:w-64 pl-10 pr-4 py-2 border rounded-lg 
                  focus:ring-2 focus:ring-[#1a9f6b] focus:border-transparent
                  text-sm transition-all ${
                    darkMode
                      ? 'bg-[#050a0a] border-[#1a2424] text-white placeholder-gray-500'
                      : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
                }`}
              />
            </div>
            <button
              onClick={onRefresh}
              disabled={refreshing || isLoading}
              className={`p-2 border rounded-lg transition-all ${
                refreshing || isLoading
                  ? 'opacity-50 cursor-not-allowed'
                  : darkMode
                    ? 'bg-[#050a0a] border-[#1a2424] text-gray-300 hover:bg-[#0a1414]'
                    : 'bg-white border-gray-300 text-[#373737] hover:bg-gray-50'
              }`}
              title="Refresh users list"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="py-8 text-center text-gray-400">Loading users...</div>
        ) : error ? (
          <div className="py-8 text-center text-red-500">{error}</div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-8 text-center text-gray-400">No users found.</div>
        ) : (
          <table className="w-full">
            <thead className={`border-b ${
              darkMode
                ? 'bg-[#050a0a] border-[#1a2424]'
                : 'bg-gray-50 border-gray-200'
            }`}>
              <tr>
                <th className={`px-4 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-400' : 'text-[#373737]'
                }`}>
                  User
                </th>
                <th className={`px-4 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-400' : 'text-[#373737]'
                }`}>
                  Role
                </th>
                <th className={`px-4 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-400' : 'text-[#373737]'
                }`}>
                  Status
                </th>
                <th className={`px-4 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-400' : 'text-[#373737]'
                }`}>
                  Designation
                </th>
                <th className={`px-4 sm:px-6 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                  darkMode ? 'text-gray-400' : 'text-[#373737]'
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'divide-[#1a2424]' : 'divide-gray-200'}`}> 
              {filteredUsers.map((user) => (
                <tr key={user.uid} className={`transition-colors ${
                  darkMode ? 'hover:bg-[#050a0a]' : 'hover:bg-gray-50'
                }`}>
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#1a9f6b] to-[#148856] 
                        rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm">
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className={`text-sm sm:text-base font-medium ${
                          darkMode ? 'text-white' : 'text-[#053333]'
                        }`}>{user.name}</p>
                        <p className={`text-xs sm:text-sm ${
                          darkMode ? 'text-gray-400' : 'text-[#373737]'
                        }`}>{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full 
                      text-xs sm:text-sm font-medium ${getRoleColor(user.role)}`}> 
                      <Shield className="w-3 h-3" />
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium ${
                      user.status === 'unsuspended'
                        ? darkMode
                          ? 'bg-green-900/30 text-green-400'
                          : 'bg-green-100 text-green-800'
                        : darkMode
                          ? 'bg-red-900/30 text-red-400'
                          : 'bg-red-100 text-red-800'
                    }`}>
                      {user.status === 'unsuspended' ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium ${
                      darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {user.designation}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-center">
                    <button
                      onClick={() => onActionClick(user)}
                      disabled={actionLoading || refreshing}
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium
                        transition-all transform active:scale-95 ${
                          actionLoading || refreshing
                            ? 'opacity-50 cursor-not-allowed'
                            : user.status === 'unsuspended'
                              ? darkMode
                                ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                              : darkMode
                                ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {user.status === 'unsuspended' ? 'Revoke Access' : 'Grant Access'}
                    </button>
                    <button
                      onClick={() => onEditClick(user)}
                      className={`ml-2 px-2 py-1 rounded-lg text-xs font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200`}
                    >
                      <Pencil className="w-4 h-4 inline" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
